import React from 'react';
import styles from './DENTToothPeriodontalLine.module.scss';
import { Scale } from '../../../Base/Interfaces/Scale.interface';
import { Tooth, ToothSide, ToothType } from '../../../../graphql/graphql';
import { ToothView } from '../../Enums/ToothView.enum';
import getNeighborTeeth from '../../Helpers/periodontalLines/getNeighborTeeth';
import getMouthViewPercentagePosition from '../../Helpers/periodontalLines/getMouthViewPercentagePosition';
import DENTToothPeriodontalLines from './DENTToothPeriodontalLines';
import useGridCalculation from '../../Hooks/Tooth/PeriodontalLines/useGridCalculation';
import usePeriodontalData from '../../Hooks/Tooth/PeriodontalLines/usePeriodontalData';
import usePeriodontalPointsMouth from '../../Hooks/Tooth/PeriodontalLines/usePeriodontalPointsMouth';
import useRenderSize from '../../Hooks/Tooth/PeriodontalLines/useRenderSize';
import { PeriodontalToothView } from '../../Interfaces/PeriodontalToothView.type';

interface Props {
	tooth: Tooth;
	scale: Scale;
	view: PeriodontalToothView;
	containerSize: { w: number; h: number };
	toothMap: Map<number, Tooth>;
}

const MOUTH_VIEW_DISTANCE_MULTIPLIER = 3;

const DENTToothPeriodontalLineMouth = ({
	tooth,
	scale,
	view,
	containerSize,
	toothMap,
}: Props) => {
	const { periodontal } = tooth;
	const isBuccal = view === ToothView.BUCCAL;
	const isLeftSide = tooth.side === ToothSide.Left;

	const { margins, depths } = usePeriodontalData({
		periodontal,
		view,
		side: tooth.side,
	});
	const { renderSize } = useRenderSize(scale);
	const { distance, isUp } = useGridCalculation({ tooth, view, renderSize });

	const positionPercent = getMouthViewPercentagePosition(tooth, view);
	const defaultY = containerSize.h * positionPercent;
	const scaledDistance = distance * MOUTH_VIEW_DISTANCE_MULTIPLIER;

	// Molars have lower offset for middle point y position
	const baseOffset =
		tooth.type === ToothType.Molar || !isBuccal
			? scaledDistance
			: 2 * scaledDistance;
	const middlePointY = defaultY + baseOffset;

	const { leftNeighbor, rightNeighbor } = getNeighborTeeth({
		tooth,
		isLeftSide,
		toothMap,
	});

	const { marginPoints, depthPoints } = usePeriodontalPointsMouth({
		base: containerSize,
		defaultY,
		middlePointY,
		distance: scaledDistance,
		margins,
		depths,
		leftNeighbor: leftNeighbor,
		rightNeighbor: rightNeighbor,
		view,
	});

	return (
		<div className={styles.wrapper}>
			<DENTToothPeriodontalLines
				viewBox={containerSize}
				containerSize={{ w: 0, h: containerSize.h }}
				isUp={isUp}
				depthPoints={depthPoints}
				marginPoints={marginPoints}
			/>
		</div>
	);
};

export default DENTToothPeriodontalLineMouth;
